import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function OnboardingTour({ onComplete, userId }) {
    const [step, setStep] = useState(0);
    const navigate = useNavigate();
    const isMobile = window.innerWidth < 768;

    const STEPS = [
        {
            title: '👋 Welcome to Career Platform!',
            description: 'Let us show you around in 60 seconds. This quick tour walks you through everything you need to get started.',
            emoji: '🎉',
        },
        {
            title: '📊 Dashboard',
            description: 'Your home base. See your application stats, recent activity, and quick actions all in one place.',
            emoji: '📊',
        },
        {
            title: '📄 Resume Analyzer',
            description: 'Upload your CV and get an ATS score, HR feedback, keyword analysis, and industry-specific tips. Costs 5 credits.',
            emoji: '📄',
        },
        {
            title: '📋 Applications Tracker',
            description: 'Track all your job applications in a Kanban board. Add interviews, notes, and deadlines.',
            emoji: '📋',
        },
        {
            title: '🧠 Career Coach',
            description: 'AI-powered career path recommendations, skill gap analysis, and a personalised learning roadmap.',
            emoji: '🧠',
        },
        {
            title: '💼 Jobs',
            description: 'Live job listings filtered by country and job type — internships, full-time, remote, graduate and more.',
            emoji: '💼',
        },
        {
            title: '📚 Study Buddy',
            description: 'Upload any document and get a detailed AI explanation. Ask follow-up questions in a chat.',
            emoji: '📚',
        },
        {
            title: '🎤 Interview Simulator',
            description: 'Practice real interviews with voice AI and get instant detailed feedback on every answer.',
            emoji: '🎤',
        },
        {
            title: '💳 Credits',
            description: 'You get 25 free credits after verifying your email. Subscribe for more — Starter ($5), Pro ($10), or Max ($20) per month.',
            emoji: '💳',
        },
        {
            title: "🚀 You're all set!",
            description: "Start by uploading your CV in the Resume Analyzer, or add your first job application. Good luck!",
            emoji: '🚀',
        },
    ];

    const current = STEPS[step];
    const isFirst = step === 0;
    const isLast = step === STEPS.length - 1;

    const handleNext = () => {
        if (step < STEPS.length - 1) setStep(step + 1);
        else handleComplete();
    };

    const handleBack = () => {
        if (step > 0) setStep(step - 1);
    };

    const handleComplete = () => {
        localStorage.setItem(`tour_completed_${userId}`, 'true');
        onComplete();
        navigate('/dashboard');
    };

    return (
        <>
            {/* Overlay */}
            <div style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 1000,
                backdropFilter: 'blur(2px)',
            }} onClick={handleComplete} />

            {/* Tooltip — always centered, never off screen */}
            <div style={{
                position: 'fixed',
                zIndex: 1002,
                // On mobile: bottom sheet
                ...(isMobile ? {
                    bottom: '80px',
                    left: '12px',
                    right: '12px',
                } : {
                    // Desktop: centered
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '360px',
                }),
                background: 'white',
                borderRadius: '16px',
                padding: '1.25rem 1.25rem 1rem',
                boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
            }}>
                {/* Progress bar */}
                <div style={{ display: 'flex', gap: '3px', marginBottom: '1rem' }}>
                    {STEPS.map((_, i) => (
                        <div key={i} style={{
                            height: '3px',
                            flex: 1,
                            borderRadius: '2px',
                            background: i <= step ? '#3b82f6' : '#e2e8f0',
                            transition: 'background 0.3s',
                        }} />
                    ))}
                </div>

                {/* Emoji + counter */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                }}>
                    <span style={{ fontSize: '1.5rem' }}>{current.emoji}</span>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600' }}>
                        {step + 1} / {STEPS.length}
                    </span>
                </div>

                {/* Title */}
                <h3 style={{
                    fontSize: isMobile ? '0.95rem' : '1rem',
                    fontWeight: '700',
                    color: '#0f172a',
                    margin: '0 0 0.4rem',
                    lineHeight: '1.4',
                }}>
                    {current.title}
                </h3>

                {/* Description */}
                <p style={{
                    fontSize: isMobile ? '0.82rem' : '0.875rem',
                    color: '#475569',
                    lineHeight: '1.6',
                    margin: '0 0 1rem',
                }}>
                    {current.description}
                </p>

                {/* Buttons */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <button
                        onClick={handleComplete}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#94a3b8',
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            padding: '0.5rem',
                        }}
                    >
                        Skip
                    </button>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {!isFirst && (
                            <button
                                onClick={handleBack}
                                style={{
                                    background: '#f1f5f9',
                                    color: '#475569',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.6rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    minHeight: '44px',
                                    minWidth: '80px',
                                }}
                            >
                                ← Back
                            </button>
                        )}
                        <button
                            onClick={handleNext}
                            style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.6rem 1.25rem',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                minHeight: '44px',
                                minWidth: '80px',
                            }}
                        >
                            {isLast ? "🚀 Let's go!" : 'Next →'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default OnboardingTour;
